import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, PLATFORM_ID, Inject, NgModule } from '@angular/core';

/* Solution from https://brianflove.com/2018/01/11/angular-window-provider/ */
/* Create a new injection token for injecting the window into a component. */
const WINDOW = new InjectionToken('OlPlatformWindowToken');
/* Define abstract class for obtaining reference to the global window object. */
class WindowRef {
    get nativeWindow() {
        throw new Error('Not implemented.');
    }
}
/* Define class that implements the abstract class and returns the native window object. */
class BrowserWindowRef extends WindowRef {
    constructor() {
        super();
    }
    get nativeWindow() {
        return window;
    }
}
BrowserWindowRef.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: BrowserWindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BrowserWindowRef.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: BrowserWindowRef });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: BrowserWindowRef, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });
/* Create an factory function that returns the native window object. */
function windowFactory(browserWindowRef, platformId) {
    if (isPlatformBrowser(platformId)) {
        return browserWindowRef.nativeWindow;
    }
    return {};
}
/* Create a injectable provider for the WindowRef token that uses the BrowserWindowRef class. */
const browserWindowProvider = {
    provide: WindowRef,
    useClass: BrowserWindowRef,
};
/* Create an injectable provider that uses the windowFactory function for returning the native window object. */
const windowProvider = {
    provide: WINDOW,
    useFactory: windowFactory,
    deps: [WindowRef, PLATFORM_ID],
};
/* Create an array of providers. */
const WINDOW_PROVIDERS = [browserWindowProvider, windowProvider];

var SegmentCode;
(function (SegmentCode) {
    SegmentCode["K12"] = "K12";
    SegmentCode["HE"] = "HE";
})(SegmentCode || (SegmentCode = {}));

class EventStream {
}

/**
 * Attributes allowed by New Relic.
 */
class NewRelicAttributes {
}
/**
 * A class that wrapps the global new relic object so
 * it can be dependency injected into Angular where
 * explicit new relic reporting is needed.
 */
class NewRelicService extends EventStream {
    constructor(window) {
        super();
        this.window = window;
    }
    /**
     * Obtains a reference to the new relic object placed on the window.
     */
    _getNewRelic() {
        this.newrelic = this.newrelic || this.window.newrelic || this.window.NREUM;
        return this.newrelic;
    }
    hasNewRelic() {
        return !!this._getNewRelic();
    }
    /**
     * Identifies a browser error without disrupting your app's operations.
     * Fails silently if new relic is not present on the window.
     */
    noticeError(error, attributes) {
        const nr = this._getNewRelic();
        if (nr) {
            nr.noticeError(error, attributes);
        }
    }
    /**
     * Reports a Browser PageAction event to Insights along with a name and attributes.
     * Fails silently if new relic is not present on the window.
     */
    addPageAction(name, attributes = {}) {
        const nr = this._getNewRelic();
        if (nr) {
            nr.addPageAction(name, attributes);
        }
    }
    /**
     * Adds a unique name and ID to identify releases with multiple JavaScript bundles on the same page.
     * @param name A short description of the component; for example, the name of a project, application, file, or library.
     * @param id The ID or version of this release; for example, a version number, build number from your CI environment, GitHub SHA, GUID, or a hash of the contents.
     * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/add-release
     */
    addRelease(name, id) {
        const nr = this._getNewRelic();
        if (nr) {
            nr.addRelease(name, id);
        }
    }
    /**
     * Adds a user-defined attribute name and value to all subsequent events on the page.
     * Fails silently if new relic is not present on the window.
     */
    setCustomAttribute(name, value) {
        const nr = this._getNewRelic();
        if (nr) {
            nr.setCustomAttribute(name, value);
        }
    }
    /**
     * Adds a set of user-defined attributes to all subsequent events on the page.
     * Fails silently if new relic is not present on the window.
     */
    setContext(values) {
        Object.entries(values).forEach(([key, value]) => {
            if (value) {
                this.setCustomAttribute(key, value.toString());
            }
        });
    }
    track(name, parameter) {
        this.addPageAction(name, parameter);
    }
}
NewRelicService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: NewRelicService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
NewRelicService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: NewRelicService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: NewRelicService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class GoogleAnalyticsService extends EventStream {
    constructor(window) {
        super();
        this.window = window;
        this.enabled = false;
        // if it exists
        this.enabled = !!this.window.ga;
    }
    /**
     * Emits custom event, such as button/link clicks to google analytics.
     * See https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference
     * @param event specifies a player event. In the event object are two required fields: eventCategory and eventAction
     */
    emitEvent(event) {
        if (!this.enabled || !event) {
            return;
        }
        if (this.window.ga) {
            this.window.ga('send', 'event', event);
        }
    }
    track(_, param) {
        this.emitEvent(param);
    }
}
GoogleAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: GoogleAnalyticsService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: GoogleAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: GoogleAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class InstrumentationModule {
}
InstrumentationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: InstrumentationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
InstrumentationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: InstrumentationModule, imports: [CommonModule] });
InstrumentationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: InstrumentationModule, providers: [...WINDOW_PROVIDERS, NewRelicService, GoogleAnalyticsService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: InstrumentationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule],
                    providers: [...WINDOW_PROVIDERS, NewRelicService, GoogleAnalyticsService],
                }]
        }] });

class ConsoleLogger {
    constructor() {
        this.context = {};
    }
    log(logLevel, message, params) {
        if (params) {
            console[logLevel](message, { ...this.context, ...params });
        }
        else {
            console[logLevel](message, this.context);
        }
    }
    setContext(params) {
        this.context = { ...this.context, ...params };
    }
}

class NewRelicLogger {
    constructor(newRelicService) {
        this.newRelicService = newRelicService;
    }
    log(logLevel, message, params) {
        if (!(message instanceof Error || logLevel === 'error')) {
            return;
        }
        /**
         * See https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
         */
        const error = message instanceof Error || (typeof message === 'object' && 'message' in message)
            ? message
            : new Error(`message: ${message}`);
        /**
         * Truncate the error message e.g. from assignment api. The max metric
         attribute value length set by New Relic is 4096 characters.
         By truncating the length of error message, we make sure errors
         can be recorded by New Relic.
         https://docs.newrelic.com/docs/data-ingest-apis/get-data-new-relic/metric-api/metric-api-limits-restricted-attributes
         */
        if (error.message.length > 500) {
            error.message = error.message.substr(0, 500);
        }
        this.newRelicService.noticeError(error, params);
    }
    setContext(values) {
        Object.entries(values).forEach(([key, value]) => {
            this.newRelicService.setCustomAttribute(key, value);
        });
    }
}

class LoggerFactory {
    constructor(newRelicService) {
        this.newRelicService = newRelicService;
    }
    createLogger() {
        return this.newRelicService.hasNewRelic()
            ? new NewRelicLogger(this.newRelicService)
            : new ConsoleLogger();
    }
}
LoggerFactory.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggerFactory, deps: [{ token: NewRelicService }], target: i0.ɵɵFactoryTarget.Injectable });
LoggerFactory.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggerFactory, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggerFactory, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: NewRelicService }]; } });

class LoggingService {
    constructor(loggerFactory) {
        this.loggerFactory = loggerFactory;
        this.logger = loggerFactory.createLogger();
    }
    setContext(values) {
        this.logger.setContext(values);
    }
    debug(message, params) {
        this.logger.log('debug', message, params);
    }
    error(message, params) {
        this.logger.log('error', message, params);
    }
    info(message, params) {
        this.logger.log('info', message, params);
    }
    trace(message, params) {
        this.logger.log('trace', message, params);
    }
    warn(message, params) {
        this.logger.log('warn', message, params);
    }
}
LoggingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggingService, deps: [{ token: LoggerFactory }], target: i0.ɵɵFactoryTarget.Injectable });
LoggingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: LoggerFactory }]; } });

class CompositeLogger {
    constructor(...backends) {
        this.backends = backends;
    }
    log(logLevel, message, params) {
        this.backends.forEach((logger) => {
            logger.log(logLevel, message, params);
        });
    }
    setContext(values) {
        this.backends.forEach((logger) => {
            logger.setContext(values);
        });
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { CompositeLogger, ConsoleLogger, EventStream, GoogleAnalyticsService, InstrumentationModule, LoggerFactory, LoggingService, NewRelicAttributes, NewRelicLogger, NewRelicService, SegmentCode, WINDOW };
